import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Box from "@material-ui/core/Box";

// Imports para filtros modernos
import {
    Grid,
    Typography,
    Chip,
    Card,
    CardContent,
} from "@material-ui/core";
import ClearIcon from "@material-ui/icons/Clear";
import FilterListIcon from "@material-ui/icons/FilterList";
import PersonIcon from "@material-ui/icons/Person";
import BusinessIcon from "@material-ui/icons/Business";
import GroupIcon from "@material-ui/icons/Group";
import PriorityHighIcon from "@material-ui/icons/PriorityHigh";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import EditIcon from "@material-ui/icons/Edit";

import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import BarChartIcon from "@material-ui/icons/BarChart";
import LockIcon from "@material-ui/icons/Lock";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import AnnouncementStatsModal from "../../components/AnnouncementStatsModal";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import AnnouncementModal from "../../components/AnnouncementModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { isArray } from "lodash";
// import { SocketContext } from "../../context/Socket/SocketContext";


import { AuthContext } from "../../context/Auth/AuthContext";

const reducer = (state, action) => {
  if (action.type === "LOAD_ANNOUNCEMENTS") {
    const announcements = action.payload;
    const newAnnouncements = [];

    if (isArray(announcements)) {
      announcements.forEach((announcement) => {
        const announcementIndex = state.findIndex(
          (u) => u.id === announcement.id
        );
        if (announcementIndex !== -1) {
          state[announcementIndex] = announcement;
        } else {
          newAnnouncements.push(announcement);
        }
      });
    }

    return [...state, ...newAnnouncements];
  }

  if (action.type === "UPDATE_ANNOUNCEMENTS") {
    const announcement = action.payload;
    const announcementIndex = state.findIndex((u) => u.id === announcement.id);

    if (announcementIndex !== -1) {
      state[announcementIndex] = announcement;
      return [...state];
    } else {
      return [announcement, ...state];
    }
  }

  if (action.type === "DELETE_ANNOUNCEMENT") {
    const announcementId = action.payload;

    const announcementIndex = state.findIndex((u) => u.id === announcementId);
    if (announcementIndex !== -1) {
      state.splice(announcementIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    // padding: theme.spacing(1),
    padding: theme.padding,
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  
  headerContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1, 0),
    width: '100%',
    minHeight: '60px',
  },
  
  filtersContainer: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: 12,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
  },
  
  searchField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 8,
      background: theme.palette.background.paper,
      '&:hover': {
        boxShadow: theme.palette.type === 'dark' ? 'none' : '0 2px 8px rgba(0,0,0,0.1)',
      },
      '&.Mui-focused': {
        boxShadow: theme.palette.type === 'dark' ? 'none' : '0 4px 16px rgba(0,0,0,0.15)',
      }
    }
  },
  
  selectField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 8,
      background: theme.palette.background.paper,
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.main,
      }
    }
  },
  
  actionButton: {
    borderRadius: 8,
    textTransform: 'none',
    fontWeight: 600,
    margin: theme.spacing(0, 0.5),
    minWidth: '140px',
    height: '40px',
  },
  
  filterChip: {
    background: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    fontWeight: 600,
    '& .MuiChip-deleteIcon': {
      color: 'rgba(255, 255, 255, 0.8)',
      '&:hover': {
        color: '#fff',
      }
    }
  },
  
  // 🎨 Estilos para os ícones de ação
  editIcon: {
    color: theme.palette.primary.main,
    transition: 'color 0.2s ease',
    '&:hover': {
      color: theme.palette.primary.dark,
    }
  },
  
  statsIcon: {
    color: theme.palette.info.main,
    transition: 'color 0.2s ease',
    '&:hover': {
      color: theme.palette.info.dark,
    }
  },
  
  deleteIcon: {
    color: theme.palette.error.main,
    transition: 'color 0.2s ease',
    '&:hover': {
      color: theme.palette.error.dark,
    }
  },
  
  // 🎨 Container para os botões de ação com separador
  actionButtonsContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(0.5),
  },
  
  // 🎨 Barrinha vertical separadora
  verticalDivider: {
    width: '1px',
    height: '24px',
    backgroundColor: theme.palette.divider,
    marginLeft: theme.spacing(0.5),
    marginRight: theme.spacing(0.5),
  },

  // 🔢 Chip do contador no título
  counterChip: {
    backgroundColor: theme.palette.action.selected,
    color: theme.palette.text.primary,
    fontWeight: 600,
    minWidth: '40px',
    height: '28px',
    fontSize: '0.875rem',
  },

  // 🧹 Botão limpar filtros
  clearButton: {
    color: theme.palette.error.main,
  },

  // 🏷️ Título da seção de filtros
  filtersTitle: {
    fontWeight: 600,
    color: theme.palette.text.primary,
    display: 'flex',
    alignItems: 'center',
  },

  // 🏷️ Chips de estado
  editableChip: {
    backgroundColor: theme.palette.action.selected,
    color: theme.palette.info.main,
    border: `1px solid ${theme.palette.divider}`,
  },
  blockedChip: {
    backgroundColor: theme.palette.action.selected,
    color: theme.palette.error.main,
    border: `1px solid ${theme.palette.divider}`,
  },
}));

const Announcements = () => {
  const classes = useStyles();
  const history = useHistory();

//   const socketManager = useContext(SocketContext);
  const { user, socket } = useContext(AuthContext);


  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [deletingAnnouncement, setDeletingAnnouncement] = useState(null);
  const [announcementModalOpen, setAnnouncementModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [announcements, dispatch] = useReducer(reducer, []);
  const [statsModalOpen, setStatsModalOpen] = useState(false);
  const [selectedAnnouncementStats, setSelectedAnnouncementStats] = useState(null);

  // 🎯 Estados dos novos filtros
  const [filters, setFilters] = useState({
    creator: "",
    targetCompany: "",
    targetAudience: "",
    priority: "",
    status: "",
    editState: ""
  });

  // 📋 Listas para os dropdowns
  const [creators, setCreators] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [activeFiltersCount, setActiveFiltersCount] = useState(0);

  // trava para nao acessar pagina que não pode  
  useEffect(() => {
    async function fetchData() {
      if (!user.super && user.profile !== "admin") {
        toast.error("Esta empresa não possui permissão para acessar essa página! Estamos lhe redirecionando.");
        setTimeout(() => {
          history.push(`/`);
        }, 1000);
      }
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam, filters]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchAnnouncements();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParam, filters, pageNumber]);

  useEffect(() => {
    if (user.companyId) {
      //    const socket = socketManager.GetSocket();

      const onCompanyAnnouncementManagement = (data) => {
        if (data.action === "update" || data.action === "create") {
          // ✅ VALIDAR se o anúncio deve aparecer na lista para este usuário
          const announcement = data.record;
          
          if (shouldShowAnnouncementInManagement(announcement, user)) {
            dispatch({ type: "UPDATE_ANNOUNCEMENTS", payload: announcement });
          }
        }
        if (data.action === "delete") {
          dispatch({ type: "DELETE_ANNOUNCEMENT", payload: +data.id });
        }
      };

      socket.on(`company-announcement-management`, onCompanyAnnouncementManagement);
      return () => {
        socket.off(`company-announcement-management`, onCompanyAnnouncementManagement);
      };
    }
  }, [user]);

  const fetchAnnouncements = async () => {
    try {
      const { data } = await api.get("/announcements/", {
        params: { 
          searchParam, 
          pageNumber,
          ...filters 
        },
      });
      dispatch({ type: "LOAD_ANNOUNCEMENTS", payload: data.records });
      setHasMore(data.hasMore);
      setLoading(false);
    } catch (err) {
      toastError(err);
    }
  };

  // 🔄 Carregar listas para os filtros
  const fetchFilterData = async () => {
    try {
      // Buscar criadores únicos
      const creatorsResponse = await api.get("/announcements/creators");
      setCreators(creatorsResponse.data || []);

      // Buscar empresas (só para empresa 1)
      if (user.companyId === 1) {
        const companiesResponse = await api.get("/companies");
        setCompanies(companiesResponse.data.companies || []);
      }
    } catch (err) {
      console.error("Erro ao carregar dados dos filtros:", err);
      // Garantir que os arrays sejam inicializados mesmo em caso de erro
      setCreators([]);
      setCompanies([]);
    }
  };

  // Carregar dados dos filtros ao montar o componente
  useEffect(() => {
    fetchFilterData();
  }, [user.companyId]);

  // Contar filtros ativos
  useEffect(() => {
    const filtersCount = Object.values(filters).filter(value => 
      value !== "" && value !== null && value !== undefined
    ).length;
    
    const searchCount = searchParam !== "" ? 1 : 0;
    
    setActiveFiltersCount(filtersCount + searchCount);
  }, [filters, searchParam]);

  const handleOpenAnnouncementModal = () => {
    setSelectedAnnouncement(null);
    setAnnouncementModalOpen(true);
  };

  const handleCloseAnnouncementModal = () => {
    setSelectedAnnouncement(null);
    setAnnouncementModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  // 🎯 Função para alterar filtros
  const handleFilterChange = (filterName) => (event) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: event.target.value
    }));
  };

  // 🧹 Função para limpar todos os filtros
  const handleClearFilters = () => {
    setFilters({
      creator: "",
      targetCompany: "",
      targetAudience: "",
      priority: "",
      status: "",
      editState: ""
    });
    setSearchParam("");
  };

  // 🔒 Verificar se anúncio pode ser editado (proteção contra fraudes)
  const canEditAnnouncement = (announcement) => {
    // Se tem publishedAt e não é agendado (isScheduled = false), não pode editar
    return !(announcement.publishedAt && !announcement.isScheduled);
  };

  const handleEditAnnouncement = (announcement) => {
    if (!canEditAnnouncement(announcement)) {
      toast.error("❌ Este anúncio já foi publicado e não pode ser editado para evitar alterações fraudulentas.");
      return;
    }
    setSelectedAnnouncement(announcement);
    setAnnouncementModalOpen(true);
  };

  const handleOpenStats = (announcement) => {
    setSelectedAnnouncementStats(announcement);
    setStatsModalOpen(true);
  };

  const handleCloseStats = () => {
    setStatsModalOpen(false);
    setSelectedAnnouncementStats(null);
  };

  const handleDeleteAnnouncement = async (announcement) => {
    try {
      if (announcement.mediaName)
        await api.delete(`/announcements/${announcement.id}/media-upload`);

      await api.delete(`/announcements/${announcement.id}`);

      toast.success(i18n.t("announcements.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingAnnouncement(null);
    setPageNumber(1);
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const translatePriority = (val) => {
    if (val === 1) {
      return "Alta";
    }
    if (val === 2) {
      return "Média";
    }
    if (val === 3) {
      return "Baixa";
    }
  };

  // Função para validar se o anúncio deve aparecer na lista de gerenciamento
  const shouldShowAnnouncementInManagement = (announcement, user) => {
    // REGRA 1: Empresa criadora deve ser a mesma do usuário
    if (announcement.createdByCompanyId !== user.companyId) {
      return false;
    }

    // REGRA 2: Admin simples da empresa 1 (não super) NÃO deve ver anúncios criados por super admin
    if (user.profile === "admin" && user.companyId === 1 && !user.super) {
      // Verificar se o anúncio foi criado por um super admin
      if (announcement.createdByUser && announcement.createdByUser.super) {
        return false;
      }
    }

    return true;
  };

  const isAnnouncementExpired = (announcement) => {
    if (!announcement.expiresAt) return false;
    return new Date() > new Date(announcement.expiresAt);
  };

  const getAnnouncementDisplayStatus = (announcement) => {
    if (isAnnouncementExpired(announcement)) {
      return {
        text: "Expirado",
        color: "#F44336", // Vermelho
        fontWeight: "bold"
      };
    }
    
    return {
      text: announcement.status ? i18n.t("announcements.active") : i18n.t("announcements.inactive"),
      color: announcement.status ? '#4CAF50' : '#9E9E9E',
      fontWeight: 'bold'
    };
  };

  // 🎯 Filtrar anúncios baseado nos filtros locais (status expirado e estado de edição)
  const filteredAnnouncements = announcements.filter(announcement => {
    // Filtro de status - incluir lógica de expirado
    if (filters.status) {
      if (filters.status === "expired" && !isAnnouncementExpired(announcement)) {
        return false;
      }
      if (filters.status === "active" && (!announcement.status || isAnnouncementExpired(announcement))) {
        return false;
      }
      if (filters.status === "inactive" && (announcement.status || isAnnouncementExpired(announcement))) {
        return false;
      }
    }

    // Filtro de estado de edição
    if (filters.editState) {
      const canEdit = canEditAnnouncement(announcement);
      if (filters.editState === "editable" && !canEdit) {
        return false;
      }
      if (filters.editState === "blocked" && canEdit) {
        return false;
      }
    }

    return true;
  });

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          deletingAnnouncement &&
          `${i18n.t("announcements.confirmationModal.deleteTitle")} ${
            deletingAnnouncement.name
          }?`
        }
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteAnnouncement(deletingAnnouncement)}
      >
        {i18n.t("announcements.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <AnnouncementModal
        resetPagination={() => {
          setPageNumber(1);
          fetchAnnouncements();
        }}
        open={announcementModalOpen}
        onClose={handleCloseAnnouncementModal}
        aria-labelledby="form-dialog-title"
        announcementId={selectedAnnouncement && selectedAnnouncement.id}
      />
      <MainHeader>
        {/* Header Moderno com Filtros Organizados */}
        <Box className={classes.headerContainer}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Title>
              {i18n.t("announcements.title")}
            </Title>
            <Chip
              label={filteredAnnouncements.length}
              size="medium"
              className={classes.counterChip}
            />
          </Box>
          
          <Button
            className={classes.actionButton}
            variant="contained"
            color="primary"
            onClick={handleOpenAnnouncementModal}
          >
            {i18n.t("announcements.buttons.add")}
          </Button>
        </Box>
      </MainHeader>

      {/* Filtros Modernos e Organizados */}
      <Card className={classes.filtersContainer} elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Typography variant="h6" className={classes.filtersTitle}>
              <FilterListIcon style={{ marginRight: 8, verticalAlign: 'middle' }} />
              Filtros de Busca
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              {activeFiltersCount > 0 && (
                <Chip 
                  label={`${activeFiltersCount} filtro${activeFiltersCount > 1 ? 's' : ''} ativo${activeFiltersCount > 1 ? 's' : ''}`}
                  size="small"
                  className={classes.filterChip}
                />
              )}
              {activeFiltersCount > 0 && (
                <Button
                  size="small"
                  onClick={handleClearFilters}
                  startIcon={<ClearIcon />}
                  className={classes.clearButton}
                >
                  Limpar ({activeFiltersCount})
                </Button>
              )}
            </Box>
          </Box>
          
          <Grid container spacing={2}>
            {/* Busca por Título */}
            <Grid item xs={12} md={4}>
              <TextField
                className={classes.searchField}
                fullWidth
                label="Buscar por título"
                variant="outlined"
                size="small"
                value={searchParam}
                onChange={handleSearch}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            {/* Criador */}
            <Grid item xs={12} md={4}>
              <FormControl variant="outlined" size="small" fullWidth className={classes.selectField}>
                <InputLabel>Criador</InputLabel>
                <Select
                  value={filters.creator}
                  onChange={handleFilterChange('creator')}
                  label="Criador"
                  startAdornment={
                    <InputAdornment position="start">
                      <PersonIcon />
                    </InputAdornment>
                  }
                >
                  <MenuItem value="">
                    <em>Todos os criadores</em>
                  </MenuItem>
                  {creators.map((creator) => (
                    <MenuItem key={creator.id} value={creator.id}>
                      {creator.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            {/* Empresa Destino (só para empresa 1) */}
            {user.companyId === 1 && (
              <Grid item xs={12} md={4}>
                <FormControl variant="outlined" size="small" fullWidth className={classes.selectField}>
                  <InputLabel>Empresa Destino</InputLabel>
                  <Select
                    value={filters.targetCompany}
                    onChange={handleFilterChange('targetCompany')}
                    label="Empresa Destino"
                    startAdornment={
                      <InputAdornment position="start">
                        <BusinessIcon />
                      </InputAdornment>
                    }
                  >
                    <MenuItem value="">
                      <em>Todas as empresas</em>
                    </MenuItem>
                    <MenuItem value="global">🌐 Global</MenuItem>
                    {companies && Array.isArray(companies) && companies.map((company) => (
                      <MenuItem key={company.id} value={company.id}>
                        🏢 {company.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            
            {/* Público-Alvo */}
            <Grid item xs={12} md={4}>
              <FormControl variant="outlined" size="small" fullWidth className={classes.selectField}>
                <InputLabel>Público-Alvo</InputLabel>
                <Select
                  value={filters.targetAudience}
                  onChange={handleFilterChange('targetAudience')}
                  label="Público-Alvo"
                  startAdornment={
                    <InputAdornment position="start">
                      <GroupIcon />
                    </InputAdornment>
                  }
                >
                  <MenuItem value="">
                    <em>Todos os públicos</em>
                  </MenuItem>
                  <MenuItem value="all">👥 Todos</MenuItem>
                  <MenuItem value="admins">👨‍💼 Admins</MenuItem>
                  <MenuItem value="users">👤 Usuários</MenuItem>
                  <MenuItem value="super">⭐ Super</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            {/* Prioridade */}
            <Grid item xs={12} md={4}>
              <FormControl variant="outlined" size="small" fullWidth className={classes.selectField}>
                <InputLabel>Prioridade</InputLabel>
                <Select
                  value={filters.priority}
                  onChange={handleFilterChange('priority')}
                  label="Prioridade"
                  startAdornment={
                    <InputAdornment position="start">
                      <PriorityHighIcon />
                    </InputAdornment>
                  }
                >
                  <MenuItem value="">
                    <em>Todas as prioridades</em>
                  </MenuItem>
                  <MenuItem value="1">🔴 Alta</MenuItem>
                  <MenuItem value="2">🟡 Média</MenuItem>
                  <MenuItem value="3">🟢 Baixa</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            {/* Status */}
            <Grid item xs={12} md={4}>
              <FormControl variant="outlined" size="small" fullWidth className={classes.selectField}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  onChange={handleFilterChange('status')}
                  label="Status"
                  startAdornment={
                    <InputAdornment position="start">
                      <CheckCircleIcon />
                    </InputAdornment>
                  }
                >
                  <MenuItem value="">
                    <em>Todos os status</em>
                  </MenuItem>
                  <MenuItem value="active">✅ Ativo</MenuItem>
                  <MenuItem value="inactive">❌ Inativo</MenuItem>
                  <MenuItem value="expired">⏰ Expirado</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            {/* Estado de Edição */}
            <Grid item xs={12} md={4}>
              <FormControl variant="outlined" size="small" fullWidth className={classes.selectField}>
                <InputLabel>Estado</InputLabel>
                <Select
                  value={filters.editState}
                  onChange={handleFilterChange('editState')}
                  label="Estado"
                  startAdornment={
                    <InputAdornment position="start">
                      <EditIcon />
                    </InputAdornment>
                  }
                >
                  <MenuItem value="">
                    <em>Todos os estados</em>
                  </MenuItem>
                  <MenuItem value="editable">✏️ Editável</MenuItem>
                  <MenuItem value="blocked">🔒 Bloqueado</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      <Paper
        className={classes.mainPaper}
        variant="outlined"
        onScroll={handleScroll}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell align="center">
                {i18n.t("announcements.table.title")}
              </TableCell>
              <TableCell align="center">
                Criador
              </TableCell>
              {user.companyId === 1 && (
                <TableCell align="center">
                  Empresa Destino
                </TableCell>
              )}
              <TableCell align="center">
                Público-Alvo
              </TableCell>
              <TableCell align="center">
                {i18n.t("announcements.table.priority")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("announcements.table.status")}
              </TableCell>
              <TableCell align="center">
                Estado
              </TableCell>
              <TableCell align="center">
                {i18n.t("announcements.table.actions")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {filteredAnnouncements.map((announcement) => (
                <TableRow key={announcement.id}>
                  <TableCell align="center">{announcement.title}</TableCell>
                  <TableCell align="center">
                    {announcement.createdByUser?.name || "N/A"}
                  </TableCell>
                  {user.companyId === 1 && (
                    <TableCell align="center">
                      {announcement.targetCompanyId === null 
                        ? "🌐 Todas as empresas" 
                        : `🏢 ${announcement.targetCompany?.name || `Empresa ${announcement.targetCompanyId}`}`
                      }
                    </TableCell>
                  )}
                  <TableCell align="center">
                    {announcement.targetAudience === "all" ? "👥 Todos" :
                     announcement.targetAudience === "admins" ? "👨‍💼 Admins" :
                     announcement.targetAudience === "users" ? "👤 Usuários" :
                     announcement.targetAudience === "super" ? "⭐ Super" : "👥 Todos"}
                  </TableCell>
                  <TableCell align="center">
                    {translatePriority(announcement.priority)}
                  </TableCell>
                  <TableCell align="center">
                    {(() => {
                      const statusInfo = getAnnouncementDisplayStatus(announcement);
                      return (
                        <span style={{ 
                          color: statusInfo.color,
                          fontWeight: statusInfo.fontWeight
                        }}>
                          {statusInfo.text}
                        </span>
                      );
                    })()}
                  </TableCell>
                  <TableCell align="center">
                    {canEditAnnouncement(announcement) ? (
                      <Chip
                        icon={<EditOutlinedIcon />}
                        label="Editável"
                        size="small"
                        className={classes.editableChip}
                      />
                    ) : (
                      <Chip
                        icon={<LockIcon />}
                        label="Bloqueado"
                        size="small"
                        className={classes.blockedChip}
                      />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <div className={classes.actionButtonsContainer}>
                      {/* Botão Editar */}
                      <IconButton
                        size="small"
                        onClick={() => handleEditAnnouncement(announcement)}
                        title={canEditAnnouncement(announcement) 
                          ? "Editar anúncio" 
                          : "Anúncio já publicado - não pode ser editado"
                        }
                        disabled={!canEditAnnouncement(announcement)}
                        style={{
                          opacity: canEditAnnouncement(announcement) ? 1 : 0.5
                        }}
                      >
                        <EditIcon 
                          className={classes.editIcon} 
                          style={{
                            color: canEditAnnouncement(announcement) 
                              ? undefined // Usa a cor do CSS (azul)
                              : '#9e9e9e' // Cinza quando desabilitado
                          }}
                        />
                      </IconButton>

                      {/* Botão Estatísticas */}
                      <IconButton
                        size="small"
                        onClick={() => handleOpenStats(announcement)}
                        title="Estatísticas"
                      >
                        <BarChartIcon className={classes.statsIcon} />
                      </IconButton>

                      {/* Barrinha vertical separadora */}
                      <div className={classes.verticalDivider}></div>

                      {/* Botão Deletar */}
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          setConfirmModalOpen(true);
                          setDeletingAnnouncement(announcement);
                        }}
                        title="Deletar anúncio"
                      >
                        <DeleteOutlineIcon className={classes.deleteIcon} />
                      </IconButton>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {loading && <TableRowSkeleton columns={user.companyId === 1 ? 9 : 8} />}
            </>
          </TableBody>
        </Table>
      </Paper>

      <AnnouncementStatsModal
        open={statsModalOpen}
        onClose={handleCloseStats}
        announcementId={selectedAnnouncementStats?.id}
      />
    </MainContainer >
  )
};

export default Announcements;
